import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { logError } from '~publish/legacy/utils/logError'

/**
 * Refreshes every cached view of the current user's access. Call it after the
 * user changes their own permissions (channel access levels or org role) so
 * the sidebar channel list, Community actions, and user menu don't keep
 * serving the pre-change state until a hard refresh (CHAN-10918).
 *
 * Permission changes ripple through too many queries to enumerate — which
 * channels `currentOrganization.channels` returns, per-channel
 * and org `allowedActions` — so this clears the whole store instead
 * of refetching a hand-picked list.
 */
export const useRefreshOwnAccess = (): (() => Promise<void>) => {
  const client = useApolloClient()

  return useCallback(async (): Promise<void> => {
    // The app-shell (top nav / user menu) runs its own Apollo client, out of
    // reach of `client.resetStore()`; this event asks it to refetch.
    window?.appshell?.actions?.refetchAccount?.()

    try {
      // Clears the cache — so screens visited later (e.g. Community) re-read
      // from the network — and refetches active queries.
      await client.resetStore()
    } catch (error) {
      // resetStore aborts every in-flight operation and its promise rejects if
      // any of them (or any refetch) fails — an AbortError from an in-flight
      // poller is routine and the store is already cleared by then, so carry
      // on to the repair step below. Only report unexpected failures.
      if (!(error instanceof Error && error.name === 'AbortError')) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          context: 'useRefreshOwnAccess',
        })
      }
    }

    try {
      // resetStore leaves queries whose fetchPolicy is currently `cache-only`
      // holding a force-emitted EMPTY result without refetching them (see
      // ObservableQuery.reset), and the sidebar channel queries pin themselves
      // to `cache-only` after their first fetch (see MainSidebar/PublishSidebar
      // `useChannels` nextFetchPolicy). Left alone they would render an empty
      // list against the now-empty cache, so refetch them directly —
      // `refetch()` forces a network request regardless of fetchPolicy.
      //
      // 'active' (not 'all') is deliberate: it includes cache-only queries —
      // it only drops `standby` — while 'all' would also refetch queries with
      // no live observers, which nothing on screen needs.
      const pinnedRefetches: Array<Promise<unknown>> = []
      client.getObservableQueries('active').forEach((observableQuery) => {
        if (observableQuery.options.fetchPolicy === 'cache-only') {
          pinnedRefetches.push(observableQuery.refetch())
        }
      })
      await Promise.all(pinnedRefetches)
    } catch (error) {
      // Failing to refresh leaves parts of the UI on stale data; report it
      // rather than surface it — the next full page load self-corrects.
      logError(error instanceof Error ? error : new Error(String(error)), {
        context: 'useRefreshOwnAccess',
      })
    }
  }, [client])
}
